import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { useInView } from "react-intersection-observer";
import ProductCard from "./ProductCard/ProductCard";
import { useProductCategoryContext } from "../contexts/ProductCategoryContext";
import { Category, ProductCategoryFilter } from "../types/Category";
import { Icon } from "./liquid/LiquidPrimitives";
import { useCategories } from "./Header/hooks/useCategories";

interface ProductListingPageProps {
  mode: "all-products" | "category" | "search";
  searchQuery?: string;
}

type ViewMode = "grid" | "list";

const DEFAULT_SORT = "newest";

const sortOptions = [
  { value: "newest", label: "Mas nuevo al mas viejo" },
  { value: "oldest", label: "Mas viejo al mas nuevo" },
  { value: "price-asc", label: "Precio: Menor a mayor" },
  { value: "price-desc", label: "Precio: Mayor a menor" },
  { value: "a-z", label: "A - Z" },
  { value: "z-a", label: "Z - A" },
];

function parseOptionalNumber(value: string): number | undefined {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const parsed = Number(trimmed.replace(/\./g, "").replace(",", "."));
  return Number.isFinite(parsed) ? parsed : undefined;
}

function sortCategories(categories: Category[]) {
  return categories
    .filter((category) => category.sku && category.nombre && category.visible !== false)
    .slice()
    .sort((a, b) => {
      const orderDiff = (a.orden ?? 999999) - (b.orden ?? 999999);
      if (orderDiff !== 0) return orderDiff;
      return a.nombre.localeCompare(b.nombre, "es", { sensitivity: "base" });
    });
}

function buildFilter(priceFrom: string, priceTo: string, sortValue: string, pageSize = 20): ProductCategoryFilter {
  const precioDesde = parseOptionalNumber(priceFrom);
  const precioHasta = parseOptionalNumber(priceTo);
  const nextFilter: ProductCategoryFilter = {
    pagina: 1,
    tamanoPagina: pageSize,
    orden: sortValue || DEFAULT_SORT,
  };

  if (precioDesde !== undefined) nextFilter.precioDesde = precioDesde;
  if (precioHasta !== undefined) nextFilter.precioHasta = precioHasta;

  return nextFilter;
}

function getSortLabel(value?: string) {
  return sortOptions.find((option) => option.value === value)?.label || sortOptions[0].label;
}

function FilterRail({
  categories,
  categoriesLoading,
  activeSku,
  priceFrom,
  priceTo,
  onPriceFromChange,
  onPriceToChange,
  onApplyPrice,
  onClearAll,
  onCategoryClick,
  hasActiveFilters,
}: {
  categories: Category[];
  categoriesLoading: boolean;
  activeSku?: string;
  priceFrom: string;
  priceTo: string;
  onPriceFromChange: (value: string) => void;
  onPriceToChange: (value: string) => void;
  onApplyPrice: () => void;
  onClearAll: () => void;
  onCategoryClick: (category: Category) => void;
  hasActiveFilters: boolean;
}) {
  return (
    <aside
      className="lg-strong"
      style={{
        padding: "28px 26px",
        borderRadius: 28,
        position: "sticky",
        top: 100,
        alignSelf: "start",
        maxHeight: "calc(100vh - 120px)",
        overflowY: "auto",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 22 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Icon.grid />
          <span style={{ fontWeight: 600, fontSize: 14, letterSpacing: "-0.01em" }}>Filtros</span>
        </div>
        <button
          type="button"
          onClick={onClearAll}
          disabled={!hasActiveFilters}
          style={{
            border: "none",
            background: "transparent",
            fontSize: 11,
            color: hasActiveFilters ? "var(--ink-500)" : "var(--ink-300)",
            cursor: hasActiveFilters ? "pointer" : "default",
            textDecoration: hasActiveFilters ? "underline" : "none",
          }}
        >
          Limpiar
        </button>
      </div>

      <div style={{ marginBottom: 26 }}>
        <div className="eyebrow" style={{ marginBottom: 12 }}>Categorias</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {categoriesLoading ? (
            <div style={{ display: "flex", alignItems: "center", gap: 10, color: "var(--ink-500)", fontSize: 13 }}>
              <CircularProgress size={14} sx={{ color: "var(--ink-500)" }} />
              Cargando categorias...
            </div>
          ) : (
            categories.map((category) => {
              const active = category.sku === activeSku;
              return (
                <button
                  key={category.id}
                  type="button"
                  onClick={() => onCategoryClick(category)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "8px 10px",
                    borderRadius: 10,
                    border: "none",
                    background: active ? "rgba(10,10,10,0.06)" : "transparent",
                    cursor: "pointer",
                    fontSize: 13,
                    textAlign: "left",
                  }}
                >
                  <span
                    style={{
                      width: 16,
                      height: 16,
                      borderRadius: 5,
                      border: "1.5px solid rgba(0,0,0,0.2)",
                      background: active ? "var(--ink-900)" : "transparent",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    {active && <Icon.check color="white" />}
                  </span>
                  <span style={{ color: active ? "var(--ink-900)" : "var(--ink-700)", fontWeight: active ? 500 : 400 }}>
                    {category.nombre}
                  </span>
                </button>
              );
            })
          )}
        </div>
      </div>

      <div>
        <div className="eyebrow" style={{ marginBottom: 12 }}>Precio</div>
        <div style={{ display: "grid", gap: 10 }}>
          <label style={{ display: "grid", gap: 6 }}>
            <span className="mono" style={{ fontSize: 10, color: "var(--ink-500)" }}>DESDE</span>
            <input
              value={priceFrom}
              onChange={(event) => onPriceFromChange(event.target.value)}
              inputMode="decimal"
              placeholder="$0"
              style={{
                height: 40,
                borderRadius: 14,
                border: "1px solid rgba(0,0,0,0.08)",
                background: "rgba(255,255,255,0.55)",
                padding: "0 12px",
                outline: "none",
                color: "var(--ink-900)",
              }}
            />
          </label>

          <label style={{ display: "grid", gap: 6 }}>
            <span className="mono" style={{ fontSize: 10, color: "var(--ink-500)" }}>HASTA</span>
            <input
              value={priceTo}
              onChange={(event) => onPriceToChange(event.target.value)}
              inputMode="decimal"
              placeholder="$999.999"
              style={{
                height: 40,
                borderRadius: 14,
                border: "1px solid rgba(0,0,0,0.08)",
                background: "rgba(255,255,255,0.55)",
                padding: "0 12px",
                outline: "none",
                color: "var(--ink-900)",
              }}
            />
          </label>

          <button type="button" className="pill" onClick={onApplyPrice} style={{ justifyContent: "center", marginTop: 4 }}>
            Aplicar precio
          </button>
        </div>
      </div>
    </aside>
  );
}

function ListingHeader({
  title,
  count,
  mode,
  breadcrumbs,
  sortValue,
  viewMode,
  onSortChange,
  onViewModeChange,
}: {
  title: string;
  count: number;
  mode: "all-products" | "category" | "search";
  breadcrumbs: { label: string; href?: string }[];
  sortValue: string;
  viewMode: ViewMode;
  onSortChange: (value: string) => void;
  onViewModeChange: (value: ViewMode) => void;
}) {
  const displayBreadcrumbs =
    mode === "search"
      ? [{ label: "Inicio", href: "/" }, { label: "Busqueda" }]
      : breadcrumbs.length > 0
      ? breadcrumbs
      : [{ label: "Inicio", href: "/" }, { label: title }];

  return (
    <div style={{ display: "flex", alignItems: "end", justifyContent: "space-between", marginBottom: 20, padding: "0 8px", gap: 24 }}>
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--ink-500)", marginBottom: 12, flexWrap: "wrap" }}>
          {displayBreadcrumbs.map((item, index) => (
            <React.Fragment key={`${item.label}-${index}`}>
              <span>{item.label}</span>
              {index < displayBreadcrumbs.length - 1 && <span style={{ opacity: .5 }}>{">"}</span>}
            </React.Fragment>
          ))}
        </div>
        <h1 style={{ fontSize: 52, fontWeight: 500, letterSpacing: "-0.035em", margin: 0, lineHeight: 1 }}>
          {title}
        </h1>
        <div style={{ marginTop: 12, fontSize: 14, color: "var(--ink-500)" }}>
          <span className="mono" style={{ fontWeight: 600, color: "var(--ink-700)" }}>{count}</span> piezas en stock
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
        <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 12, color: "var(--ink-500)" }}>Ordenar por</span>
          <select
            value={sortValue}
            onChange={(event) => onSortChange(event.target.value)}
            className="pill"
            style={{
              appearance: "none",
              padding: "10px 34px 10px 16px",
              color: "var(--ink-700)",
              border: "1px solid rgba(255,255,255,0.52)",
              outline: "none",
              cursor: "pointer",
            }}
          >
            {sortOptions.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </label>

        <div style={{ display: "flex", padding: 4, borderRadius: 999, background: "rgba(10,10,10,0.06)" }}>
          <button
            type="button"
            onClick={() => onViewModeChange("grid")}
            style={{
              padding: "8px 12px",
              borderRadius: 999,
              border: "none",
              background: viewMode === "grid" ? "var(--ink-900)" : "transparent",
              color: viewMode === "grid" ? "var(--cream-50)" : "var(--ink-500)",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 4,
              fontSize: 12,
              fontWeight: viewMode === "grid" ? 500 : 400,
            }}
          >
            <Icon.grid /> Grid
          </button>
          <button
            type="button"
            onClick={() => onViewModeChange("list")}
            style={{
              padding: "8px 12px",
              borderRadius: 999,
              border: "none",
              background: viewMode === "list" ? "var(--ink-900)" : "transparent",
              color: viewMode === "list" ? "var(--cream-50)" : "var(--ink-500)",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 4,
              fontSize: 12,
              fontWeight: viewMode === "list" ? 500 : 400,
            }}
          >
            <Icon.list /> Lista
          </button>
        </div>
      </div>
    </div>
  );
}

function ActiveFilters({
  chips,
}: {
  chips: { key: string; label: string; onClear?: () => void }[];
}) {
  if (chips.length === 0) return null;

  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 24, padding: "0 8px" }}>
      {chips.map((chip) => (
        <button
          key={chip.key}
          type="button"
          className="pill"
          onClick={chip.onClear}
          style={{ padding: "8px 14px", fontSize: 12, cursor: chip.onClear ? "pointer" : "default" }}
        >
          {chip.label}
          {chip.onClear && <Icon.close />}
        </button>
      ))}
    </div>
  );
}

const ProductListingPage: React.FC<ProductListingPageProps> = ({ mode, searchQuery }) => {
  const { sku } = useParams<{ sku: string }>();
  const navigate = useNavigate();
  const [ref, inView] = useInView({ threshold: 0, rootMargin: "100px" });
  const { categories, loading: categoriesLoading } = useCategories();
  const [sortValue, setSortValue] = useState(DEFAULT_SORT);
  const [priceFrom, setPriceFrom] = useState("");
  const [priceTo, setPriceTo] = useState("");
  const [viewMode, setViewMode] = useState<ViewMode>("grid");

  const {
    category,
    products,
    loading,
    loadingMore,
    error,
    breadcrumbs,
    pagination,
    filter,
    loadCategoryData,
    loadAllProductsData,
    loadSearchResults,
    loadMoreProducts,
    saveListingCache,
  } = useProductCategoryContext();

  const sortedCategories = useMemo(() => sortCategories(categories), [categories]);

  const applyFilter = async (
    nextPriceFrom = priceFrom,
    nextPriceTo = priceTo,
    nextSort = sortValue,
  ) => {
    const nextFilter = buildFilter(nextPriceFrom, nextPriceTo, nextSort, filter.tamanoPagina || 20);

    if (mode === "all-products") {
      await loadAllProductsData(nextFilter);
    } else if (mode === "category" && sku) {
      await loadCategoryData(sku, nextFilter);
    } else if (mode === "search" && searchQuery) {
      await loadSearchResults(searchQuery, nextFilter);
    }
  };

  useEffect(() => {
    const initialSort = DEFAULT_SORT;
    setSortValue(initialSort);
    setPriceFrom("");
    setPriceTo("");

    const initialFilter = buildFilter("", "", initialSort, filter.tamanoPagina || 20);

    if (mode === "all-products") {
      loadAllProductsData(initialFilter);
    } else if (mode === "category" && sku) {
      loadCategoryData(sku, initialFilter);
    } else if (mode === "search" && searchQuery) {
      loadSearchResults(searchQuery, initialFilter);
    }
  }, [mode, sku, searchQuery]);

  useEffect(() => {
    if (inView && !loading && !loadingMore && pagination.actualPage < pagination.cantPages) {
      loadMoreProducts();
    }
  }, [inView, loading, loadingMore, pagination.actualPage, pagination.cantPages, loadMoreProducts]);

  const handleProductClick = (productSku: string) => {
    const cacheKey = mode === "category" ? `category:${sku}` : mode === "search" ? `search:${searchQuery}` : "products";
    saveListingCache(cacheKey);
    navigate(`/productos/${productSku}`, { state: mode === "category" ? { fromCategorySku: sku } : undefined });
  };

  const handleSortChange = (nextSort: string) => {
    setSortValue(nextSort);
    applyFilter(priceFrom, priceTo, nextSort);
  };

  const handleClearAll = () => {
    setPriceFrom("");
    setPriceTo("");
    setSortValue(DEFAULT_SORT);
    if (mode === "category") {
      navigate("/productos");
      return;
    }
    applyFilter("", "", DEFAULT_SORT);
  };

  const title =
    mode === "all-products"
      ? "Productos"
      : mode === "search"
      ? searchQuery
        ? `Busqueda: ${searchQuery}`
        : "Busqueda"
      : category?.nombre || (sku ? sku.replace(/-/g, " ").toUpperCase() : "");

  const noProductsMessage =
    mode === "all-products"
      ? "No se encontraron productos"
      : mode === "search"
      ? `No se encontraron resultados para "${searchQuery}"`
      : "No se encontraron productos en esta categoria";

  const activeChips = useMemo(() => {
    const chips: { key: string; label: string; onClear?: () => void }[] = [];

    if (mode === "category" && category) {
      chips.push({ key: "category", label: `Categoria: ${category.nombre}`, onClear: () => navigate("/productos") });
    }

    if (mode === "search" && searchQuery) {
      chips.push({ key: "search", label: `Busqueda: ${searchQuery}` });
    }

    if (filter.precioDesde !== undefined) {
      chips.push({
        key: "from",
        label: `Desde $${filter.precioDesde.toLocaleString("es-AR")}`,
        onClear: () => {
          setPriceFrom("");
          applyFilter("", priceTo, sortValue);
        },
      });
    }

    if (filter.precioHasta !== undefined) {
      chips.push({
        key: "to",
        label: `Hasta $${filter.precioHasta.toLocaleString("es-AR")}`,
        onClear: () => {
          setPriceTo("");
          applyFilter(priceFrom, "", sortValue);
        },
      });
    }

    if (filter.orden) {
      chips.push({
        key: "sort",
        label: `Orden: ${getSortLabel(filter.orden)}`,
        onClear: filter.orden !== DEFAULT_SORT ? () => {
          setSortValue(DEFAULT_SORT);
          applyFilter(priceFrom, priceTo, DEFAULT_SORT);
        } : undefined,
      });
    }

    return chips;
  }, [mode, category, searchQuery, filter.precioDesde, filter.precioHasta, filter.orden, navigate, priceFrom, priceTo, sortValue]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress size={24} sx={{ color: "var(--ink-900)" }} />
        <Typography sx={{ ml: 2, color: "var(--ink-500)", fontSize: "0.9rem" }}>Cargando productos...</Typography>
      </Box>
    );
  }

  if (error && products.length === 0) {
    return (
      <Box sx={{ py: 8, textAlign: "center", px: 2 }}>
        <Typography sx={{ color: "var(--ink-900)", fontWeight: 600, mb: 1 }}>{error}</Typography>
        <Typography sx={{ color: "var(--ink-500)", fontSize: "0.9rem", mb: 3 }}>No pudimos cargar los productos.</Typography>
        <Button variant="contained" onClick={() => applyFilter()}>
          Reintentar
        </Button>
      </Box>
    );
  }

  if (mode === "search" && !searchQuery) {
    return (
      <Box sx={{ py: 8, textAlign: "center" }}>
        <Typography sx={{ fontWeight: 600, color: "var(--ink-900)", mb: 1 }}>Busqueda vacia</Typography>
        <Typography sx={{ color: "var(--ink-500)", fontSize: "0.9rem" }}>Por favor, ingresa un termino de busqueda.</Typography>
      </Box>
    );
  }

  const hasActiveFilters =
    mode === "category" ||
    !!priceFrom ||
    !!priceTo ||
    sortValue !== DEFAULT_SORT ||
    filter.precioDesde !== undefined ||
    filter.precioHasta !== undefined ||
    filter.orden !== DEFAULT_SORT;

  return (
    <main style={{ padding: "32px 32px 60px", position: "relative", zIndex: 2 }}>
      <ListingHeader
        title={title}
        count={pagination.cantTotal || products.length}
        mode={mode}
        breadcrumbs={breadcrumbs}
        sortValue={sortValue}
        viewMode={viewMode}
        onSortChange={handleSortChange}
        onViewModeChange={setViewMode}
      />

      <ActiveFilters chips={activeChips} />

      <div style={{ display: "grid", gridTemplateColumns: "300px 1fr", gap: 28 }}>
        <FilterRail
          categories={sortedCategories}
          categoriesLoading={categoriesLoading}
          activeSku={mode === "category" ? sku : undefined}
          priceFrom={priceFrom}
          priceTo={priceTo}
          onPriceFromChange={setPriceFrom}
          onPriceToChange={setPriceTo}
          onApplyPrice={() => applyFilter()}
          onClearAll={handleClearAll}
          onCategoryClick={(nextCategory) => navigate(`/categoria/${nextCategory.sku}`)}
          hasActiveFilters={hasActiveFilters}
        />

        <div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: viewMode === "grid" ? "repeat(3, minmax(0, 1fr))" : "1fr",
              gap: viewMode === "grid" ? 18 : 14,
            }}
          >
            {products.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                variant="showcase-small"
                layout={viewMode === "grid" ? "card" : "row"}
                onClick={() => handleProductClick(product.sku)}
              />
            ))}
          </div>

          {(loadingMore || pagination.actualPage < pagination.cantPages) && (
            <div ref={ref} style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 12, marginTop: 48, padding: 24 }}>
              <CircularProgress size={20} sx={{ color: "var(--ink-900)" }} />
              <span style={{ color: "var(--ink-500)", fontSize: 13 }}>Cargando mas productos...</span>
            </div>
          )}

          {error && products.length > 0 && (
            <div style={{ textAlign: "center", marginTop: 32 }}>
              <button className="pill" onClick={loadMoreProducts}>Reintentar</button>
            </div>
          )}

          {!loading && products.length === 0 && !error && (
            <div style={{ textAlign: "center", marginTop: 64, color: "var(--ink-500)" }}>{noProductsMessage}</div>
          )}
        </div>
      </div>
    </main>
  );
};

export default ProductListingPage;
